import { formatDateRangePt } from "../../../dashboard-framework/describeWidgetLogic";
import { DateFilterKey, DateRange } from "../../../dashboard-framework/types";
import { ROLES } from "../../../utils/constants";

// Pedido real do stakeholder (Jorge, 12/06/2026) — critérios e decisões em spec.md.
// Tudo client-side a partir de fetches raw: groupBy aninhado por M2M dá 500 no Directus
// e counts agregados sob row-scope duplicam no JOIN da junção (ver spec.md).

export const TEAM_TASKS_KEY = "team-tasks-productivity";

// Limiar de sobrecarga (regra 1 do stakeholder): > 15 abertas fica a vermelho. Fixo em código.
export const TEAM_TASKS_RED_OPEN_THRESHOLD = 15;

// "Em aberto" inclui status null — convenção do próprio ecrã de tarefas (tasksOptions.tsx).
export const OPEN_STATUS_FILTER = { _or: [{ status: { _eq: "open" } }, { status: { _null: true } }] };
export const DONE_STATUS_FILTER = { status: { _eq: "done" } };

export type TeamFunctionKey = "neurofeedback" | "psicologia" | "apoio_cliente" | "outros";
export type TeamFunctionFilterKey = "todas" | TeamFunctionKey;
export type TaskStatusFilterKey = "abertas" | "feitas" | "todas";

// Datas reais da tarefa. "conclusao" usa o campo done_date (preenchido pelo hook do backend
// quando o estado passa a "done"); só existe para tarefas concluídas a partir da sua criação.
export type TaskDateModeKey = "criacao" | "deadline" | "conclusao";

// Réplica do agrupamento por função dos schedules (SchedulesUserFilter.processUserSelection);
// "outros" aqui é disjunto do apoio ao cliente para os cartões não duplicarem (spec.md).
export const CUSTOMER_SUPPORT_ROLE_IDS: Array<string> = [
    ROLES.recepcionist,
    ROLES.reception_director,
    ROLES.callcenter,
    ROLES.back_office,
];

export const FUNCTION_OPTIONS: Array<{ key: TeamFunctionKey; label: string }> = [
    { key: "neurofeedback", label: "Neurofeedback" },
    { key: "psicologia", label: "Psicologia" },
    { key: "apoio_cliente", label: "Apoio ao cliente" },
    { key: "outros", label: "Outros" },
];

export const STATUS_FILTER_OPTIONS: Array<{ key: TaskStatusFilterKey; label: string }> = [
    { key: "abertas", label: "Em aberto" },
    { key: "feitas", label: "Feitas" },
    { key: "todas", label: "Todas" },
];

export const DATE_MODE_OPTIONS: Array<{ key: TaskDateModeKey; label: string }> = [
    { key: "conclusao", label: "Conclusão" },
    { key: "criacao", label: "Criação" },
    { key: "deadline", label: "Deadline" },
];

// Subset de presets do filtro partilhado (DashboardDateFilter acrescenta sempre "Personalizado").
// sem_periodo (default) resolve para null: abertas são o stock total e as FEITAS não são
// contadas nem fetchadas (todo o histórico não é um universo limitável).
export const TEAM_TASKS_PERIOD_PRESETS: Array<DateFilterKey> = [
    "sem_periodo",
    "ultimos_7_dias",
    "ultimos_30_dias",
    "esta_semana",
    "este_mes",
    "ultimos_3_meses",
];

export const OPEN_TASKS_FIELDS = "id,due_date,date_created,assigned_users.directus_users_id";

export const DONE_TASKS_FIELDS = "id,date_created,due_date,done_date,assigned_users.directus_users_id";

export const PEOPLE_FIELDS = "id,first_name,last_name,email,status,role,technician_id.specializations";

// A rota /users (createRoute) ignora limit/page — responde sempre com os primeiros 100.
// O lookup de pessoas é feito em blocos de ids abaixo desse teto.
export const PEOPLE_LOOKUP_CHUNK_SIZE = 80;

export const TASKS_DRILL_FIELDS =
    "id,content,status,due_date,date_created,done_date,assigned_users.directus_users_id.first_name,assigned_users.directus_users_id.last_name";

const dateClauseForMode = (dateMode: TaskDateModeKey, range: DateRange | null) => {
    if (!range) return null;
    if (dateMode === "deadline") return { due_date: { _gte: range.gte, _lt: range.lt } };
    if (dateMode === "conclusao") return { done_date: { _gte: range.gte, _lt: range.lt } };
    const createdClause = { date_created: { _gte: range.gte, _lt: range.lt } };
    return createdClause;
};

// Sem período as abertas são o stock total. Em modo "conclusão" as abertas também são o stock:
// não têm data de conclusão, logo não são recortadas pelo período (só as feitas o são).
export const openTasksFilter = (dateMode: TaskDateModeKey, range: DateRange | null) => {
    const dateClause = dateMode === "conclusao" ? null : dateClauseForMode(dateMode, range);
    const filter = dateClause ? { _and: [OPEN_STATUS_FILTER, dateClause] } : OPEN_STATUS_FILTER;
    return filter;
};

export const doneTasksFilter = (dateMode: TaskDateModeKey, range: DateRange) => {
    const filter = { _and: [DONE_STATUS_FILTER, dateClauseForMode(dateMode, range)] };
    return filter;
};

// Vista pessoal (viewer_rules → params.taskUserId): só as tarefas atribuídas ao próprio.
export const taskViewerFilter = (viewerUserId: string) => {
    const filter = { assigned_users: { directus_users_id: { _eq: viewerUserId } } };
    return filter;
};

const labelOf = <KeyType extends string>(options: Array<{ key: KeyType; label: string }>, key: KeyType) => {
    const option = options.find((candidate) => candidate.key === key);
    const label = option?.label || key;
    return label;
};

const functionScopeText = (functionFilter: TeamFunctionFilterKey) => {
    const text =
        functionFilter === "todas"
            ? "toda a equipa (agrupamento por função igual ao dos schedules)"
            : `colaboradores da função "${labelOf(FUNCTION_OPTIONS, functionFilter)}" (agrupamento igual ao dos schedules)`;
    return text;
};

const dateModeText = (dateMode: TaskDateModeKey, range: DateRange) => {
    if (dateMode === "deadline") return `com deadline ${formatDateRangePt(range)}`;
    if (dateMode === "conclusao") return `concluídas ${formatDateRangePt(range)}`;
    const text = `criadas ${formatDateRangePt(range)}`;
    return text;
};

// Explicações ⓘ — geradas a partir dos MESMOS parâmetros das queries (nunca texto solto).
export const describeOpenTotalPt = (dateMode: TaskDateModeKey, range: DateRange | null, functionFilter: TeamFunctionFilterKey) => {
    // Em "conclusão" as abertas não são recortadas (não têm data de conclusão) — são sempre stock.
    const isStockView = !range || dateMode === "conclusao";
    const periodText = isStockView ? "stock atual, sem recorte temporal" : dateModeText(dateMode, range);
    const description =
        `Tarefas com estado "Aberta" (ou sem estado — tratadas como abertas, tal como no ecrã de tarefas), ${periodText}, ` +
        `de ${functionScopeText(functionFilter)}. Cada tarefa conta uma só vez, mesmo com vários atribuídos; ` +
        `tarefas sem responsável contam apenas com a função em "todas".`;
    return description;
};

// Nota acrescentada a todas as explicações quando a vista é pessoal (viewer_rules).
export const withOwnViewNotePt = (description: string, isOwnView: boolean) => {
    if (!isOwnView) return description;
    const noted = `${description} Vista pessoal: só as tarefas atribuídas a ti.`;
    return noted;
};

export const describeNonActiveGroupCardPt = () => {
    const description =
        "Tarefas cujos responsáveis são TODOS utilizadores inativos (ex-colaboradores) ou sem identificação — " +
        "sem dono ativo, candidatas a reatribuição. Uma tarefa partilhada com alguém ativo conta no ativo, não aqui. " +
        "Somado aos cartões de função e ao 'Sem responsável', dá exatamente o total global.";
    return description;
};

export const describeUnassignedCardPt = () => {
    const description =
        "Tarefas sem nenhum responsável atribuído. Somado aos cartões de função e ao 'Inativos / sem identificação', " +
        "dá exatamente o total global.";
    return description;
};

// Sobreposição = tarefas contadas em mais do que um cartão de função (pessoas com dupla
// especialização ou multi-atribuição entre funções). O número é calculado, nunca estimado.
export const functionOverlapNotePt = (overlapCount: number) => {
    const note =
        `Nota: ${overlapCount === 1 ? "1 tarefa está" : `${overlapCount} tarefas estão`} em mais do que uma função ` +
        "(dupla especialização ou multi-atribuição) — contam em cada cartão, mas uma só vez no total.";
    return note;
};

export const describeDoneTotalPt = (dateMode: TaskDateModeKey, range: DateRange | null, functionFilter: TeamFunctionFilterKey) => {
    if (!range) {
        const noPeriodDescription =
            "Sem período escolhido, as tarefas feitas não são contadas (todo o histórico não é um universo útil). " +
            "Escolhe um período no filtro para as veres.";
        return noPeriodDescription;
    }
    const description =
        `Tarefas com estado "Concluída", ${dateModeText(dateMode, range)}, de ${functionScopeText(functionFilter)}. ` +
        `Cada tarefa conta uma só vez, mesmo com vários atribuídos.`;
    return description;
};

export const describeRedCountPt = (functionFilter: TeamFunctionFilterKey) => {
    const description =
        `Colaboradores de ${functionScopeText(functionFilter)} com mais de ${TEAM_TASKS_RED_OPEN_THRESHOLD} tarefas em aberto ` +
        `na vista atual (limiar fixo, igual para todas as funções). A linha "Sem responsável" nunca conta.`;
    return description;
};

export const describeFunctionCardsPt = (dateMode: TaskDateModeKey, range: DateRange | null) => {
    const doneText = range
        ? `"feitas" = ${dateModeText(dateMode, range)}`
        : `sem período escolhido, as feitas não são contadas — escolhe um período para as veres`;
    const description =
        `Acumulados por função — agrupamento de colaboradores igual ao dos schedules (especialização de técnico para ` +
        `Neurofeedback/Psicologia, role para Apoio ao cliente, restantes em Outros; quem tem dupla especialização conta nas duas). ` +
        `"Em aberto" = tarefas abertas atribuídas a colaboradores da função; ${doneText}. ` +
        `Dentro de cada cartão, cada tarefa conta uma só vez.`;
    return description;
};

export const describeTablePt = (statusFilter: TaskStatusFilterKey, dateMode: TaskDateModeKey, range: DateRange | null) => {
    if (!range) {
        const noPeriodDescription =
            `Uma linha por colaborador com tarefas em aberto (stock total — sem período, uma tarefa aberta há um ano continua por fazer), ` +
            `ordenada por tarefas em aberto (decrescente). Multi-atribuídas contam para cada colaborador (a soma das linhas pode exceder ` +
            `os totais, que têm dedup). Linhas acima de ${TEAM_TASKS_RED_OPEN_THRESHOLD} abertas ficam a vermelho. ` +
            `Clicar numa linha mostra as tarefas (só leitura). Escolhe um período para aparecerem as feitas.`;
        return noPeriodDescription;
    }
    const sortText = statusFilter === "feitas" ? "feitas no período" : "tarefas em aberto";
    const description =
        `Uma linha por colaborador com tarefas no estado "${labelOf(STATUS_FILTER_OPTIONS, statusFilter)}" da vista atual, ` +
        `ordenada por ${sortText} (decrescente). "Em aberto" = tarefas abertas atribuídas; "Feitas no período" = ${dateModeText(
            dateMode,
            range
        )}. Multi-atribuídas contam para cada colaborador (a soma das linhas pode exceder os totais, que têm dedup). ` +
        `Linhas acima de ${TEAM_TASKS_RED_OPEN_THRESHOLD} abertas ficam a vermelho. Clicar numa linha mostra as tarefas (só leitura).`;
    return description;
};

export const describeWeeklyChartPt = (dateMode: TaskDateModeKey, range: DateRange, personLabel: string | null) => {
    const scopeText = personLabel ? `do colaborador ${personLabel}` : "da equipa filtrada";
    const description =
        `Tarefas concluídas por semana ISO (segunda a domingo), ${dateModeText(dateMode, range)}, ${scopeText}. ` +
        `A soma dos pontos bate com o cartão "Feitas no período" (mesmo universo e mesma data).`;
    return description;
};
